import React from 'react';
import { motion } from 'framer-motion';
import { Shield, ArrowLeft, Mail, MapPin, Building2, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  const lastUpdate = "19 de febrero de 2026";
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Link to="/" className="inline-flex items-center text-slate-400 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al inicio
          </Link>
          
          <div className="flex items-center space-x-4 mb-4">
            <div className="p-3 bg-blue-500/20 rounded-xl">
              <Shield className="w-8 h-8 text-blue-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Política de Privacidad</h1>
              <p className="text-slate-400">Última actualización: {lastUpdate}</p>
            </div>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700 space-y-8"
        >
          {/* Introducción */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">1. Introducción</h2>
            <p className="text-slate-300 leading-relaxed">
              NotaryOS ("nosotros", "nuestro" o "la Plataforma") se compromete a proteger la privacidad 
              de sus usuarios. Esta Política de Privacidad describe cómo recopilamos, usamos, almacenamos 
              y protegemos su información personal en cumplimiento con la <strong>Ley N° 19.628 sobre 
              Protección de la Vida Privada</strong> de Chile y demás normativa aplicable.
            </p>
          </section>

          {/* Responsable del Tratamiento */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">2. Responsable del Tratamiento de Datos</h2>
            <div className="bg-slate-700/50 rounded-xl p-4 space-y-2">
              <div className="flex items-center space-x-2 text-slate-300">
                <Building2 className="w-4 h-4 text-blue-400" />
                <span><strong>Razón Social:</strong> NotaryOS SpA</span>
              </div>
              <div className="flex items-center space-x-2 text-slate-300">
                <FileText className="w-4 h-4 text-blue-400" />
                <span><strong>RUT:</strong> [Por configurar]</span>
              </div>
              <div className="flex items-center space-x-2 text-slate-300">
                <MapPin className="w-4 h-4 text-blue-400" />
                <span><strong>Dirección:</strong> Santiago, Chile</span>
              </div>
              <div className="flex items-center space-x-2 text-slate-300">
                <Mail className="w-4 h-4 text-blue-400" />
                <span><strong>Contacto:</strong> privacidad@notaryos.cl</span>
              </div>
            </div>
          </section>

          {/* Datos que Recopilamos */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">3. Datos Personales que Recopilamos</h2>
            <p className="text-slate-300 mb-4">Recopilamos los siguientes tipos de datos personales:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li><strong>Datos de identificación:</strong> Nombre completo, RUT, correo electrónico</li>
              <li><strong>Datos de contacto:</strong> Teléfono, dirección postal</li>
              <li><strong>Datos de uso:</strong> Información sobre cómo utiliza nuestra plataforma</li>
              <li><strong>Datos contractuales:</strong> Contratos creados, firmados y gestionados</li>
              <li><strong>Datos técnicos:</strong> Dirección IP, tipo de navegador, dispositivo</li>
              <li><strong>Datos biométricos:</strong> Firma digital (solo con su consentimiento expreso)</li>
            </ul>
          </section>

          {/* Finalidad del Tratamiento */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">4. Finalidad del Tratamiento</h2>
            <p className="text-slate-300 mb-4">Sus datos personales serán tratados para:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Proveer los servicios de generación y gestión de contratos</li>
              <li>Verificar su identidad para firma de documentos</li>
              <li>Enviar notificaciones sobre sus contratos</li>
              <li>Mejorar nuestros servicios mediante análisis de uso</li>
              <li>Cumplir con obligaciones legales y requerimientos judiciales</li>
              <li>Prevenir fraudes y garantizar la seguridad de la plataforma</li>
            </ul>
          </section>

          {/* Base Legal */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">5. Base Legal del Tratamiento</h2>
            <p className="text-slate-300 leading-relaxed">
              El tratamiento de sus datos se basa en: (a) su <strong>consentimiento expreso</strong> otorgado 
              al registrarse; (b) la <strong>ejecución de un contrato</strong> del cual usted es parte; 
              (c) el cumplimiento de <strong>obligaciones legales</strong>; y (d) nuestros <strong>intereses 
              legítimos</strong> para mejorar y asegurar nuestros servicios, siempre que no prevalezcan 
              sus derechos y libertades fundamentales.
            </p>
          </section>

          {/* Derechos ARCO */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">6. Sus Derechos (Derechos ARCO)</h2>
            <p className="text-slate-300 mb-4">
              Conforme a la Ley N° 19.628, usted tiene los siguientes derechos:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-slate-700/50 rounded-xl p-4">
                <h3 className="font-semibold text-blue-400 mb-2">Acceso</h3>
                <p className="text-slate-300 text-sm">
                  Derecho a conocer qué datos personales suyos están siendo tratados.
                </p>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-4">
                <h3 className="font-semibold text-green-400 mb-2">Rectificación</h3>
                <p className="text-slate-300 text-sm">
                  Derecho a solicitar la corrección de datos inexactos o incompletos.
                </p>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-4">
                <h3 className="font-semibold text-orange-400 mb-2">Cancelación</h3>
                <p className="text-slate-300 text-sm">
                  Derecho a solicitar la eliminación de sus datos cuando ya no sean necesarios.
                </p>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-4">
                <h3 className="font-semibold text-red-400 mb-2">Oposición</h3>
                <p className="text-slate-300 text-sm">
                  Derecho a oponerse al tratamiento de sus datos en ciertas circunstancias.
                </p>
              </div>
            </div>
            <p className="text-slate-300 mt-4">
              Para ejercer estos derechos, envíe un correo a <strong>privacidad@notaryos.cl</strong> con 
              su solicitud y una copia de su documento de identidad. Responderemos en un plazo máximo 
              de 15 días hábiles.
            </p>
          </section>

          {/* Seguridad */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">7. Seguridad de los Datos</h2>
            <p className="text-slate-300 leading-relaxed">
              Implementamos medidas técnicas y organizativas apropiadas para proteger sus datos personales, 
              incluyendo: encriptación AES-256 para datos en reposo, conexiones TLS 1.3 para datos en 
              tránsito, controles de acceso basados en roles, auditorías de seguridad periódicas, y 
              copias de seguridad encriptadas.
            </p>
          </section>

          {/* Retención */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">8. Período de Retención</h2>
            <p className="text-slate-300 leading-relaxed">
              Conservamos sus datos personales durante el tiempo necesario para cumplir con las 
              finalidades descritas, y posteriormente durante los plazos legales aplicables. Los 
              contratos y documentos firmados se conservan por un mínimo de 10 años conforme a la 
              legislación mercantil chilena.
            </p>
          </section>

          {/* Transferencias Internacionales */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">9. Transferencias Internacionales</h2>
            <p className="text-slate-300 leading-relaxed">
              Sus datos pueden ser transferidos a servidores ubicados fuera de Chile (servicios de 
              nube como AWS o Google Cloud). En tales casos, nos aseguramos de que existan garantías 
              adecuadas de protección de datos conforme a estándares internacionales.
            </p>
          </section>

          {/* Cambios */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">10. Cambios a esta Política</h2>
            <p className="text-slate-300 leading-relaxed">
              Podemos actualizar esta Política de Privacidad periódicamente. Le notificaremos cualquier 
              cambio material por correo electrónico o mediante un aviso en nuestra plataforma. Le 
              recomendamos revisar esta política regularmente.
            </p>
          </section>

          {/* Contacto */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">11. Contacto</h2>
            <p className="text-slate-300 leading-relaxed">
              Si tiene preguntas sobre esta Política de Privacidad o sobre el tratamiento de sus datos 
              personales, contáctenos en:
            </p>
            <div className="mt-4 bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
              <p className="text-blue-400">
                <strong>Email:</strong> privacidad@notaryos.cl<br />
                <strong>Formulario:</strong> Sección "Soporte" en la plataforma
              </p>
            </div>
          </section>
        </motion.div>

        {/* Footer */}
        <div className="mt-8 text-center text-slate-500 text-sm">
          © 2026 NotaryOS SpA. Todos los derechos reservados.
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
